import React, { useCallback, useId, useRef } from 'react';
import type { CustomField } from '@puckeditor/core';
import { FieldLabelWithHelp } from './field-label-with-help';

import { IMAGE_FOCAL_POINTS } from '../tokens';
import type { ImageFocalPoint } from '../tokens';

type FocalPointFieldProps = {
  field: { label?: string };
  name: string;
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  hint?: string;
};

/** Grid position, label, and row/col for each of the nine focal points. */
const GRID_CELLS: { key: ImageFocalPoint; label: string; row: number; col: number }[] = [
  { key: 'topLeft', label: 'Top left', row: 0, col: 0 },
  { key: 'topCenter', label: 'Top center', row: 0, col: 1 },
  { key: 'topRight', label: 'Top right', row: 0, col: 2 },
  { key: 'centerLeft', label: 'Center left', row: 1, col: 0 },
  { key: 'center', label: 'Center', row: 1, col: 1 },
  { key: 'centerRight', label: 'Center right', row: 1, col: 2 },
  { key: 'bottomLeft', label: 'Bottom left', row: 2, col: 0 },
  { key: 'bottomCenter', label: 'Bottom center', row: 2, col: 1 },
  { key: 'bottomRight', label: 'Bottom right', row: 2, col: 2 },
];

const FOCAL_POINT_KEYS = Object.keys(IMAGE_FOCAL_POINTS) as ImageFocalPoint[];

/**
 * Reads a saved focal-point key defensively. Saved page data is unvalidated JSON, so a persisted
 * value may not be one of the nine current keys; treat anything unrecognised as unselected rather
 * than crashing the grid or asserting a cell that was not actually chosen.
 */
function isFocalPoint(value: string): value is ImageFocalPoint {
  return (FOCAL_POINT_KEYS as string[]).includes(value);
}

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gridTemplateRows: 'repeat(3, 1fr)',
  gap: 4,
  width: 96,
  height: 96,
};

function cellStyle(selected: boolean, readOnly: boolean | undefined): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${selected ? '#2563eb' : '#d0d4d8'}`,
    borderRadius: 4,
    background: selected ? '#eff4ff' : '#fff',
    cursor: readOnly ? 'default' : 'pointer',
    padding: 0,
  };
}

const dotStyle = (selected: boolean): React.CSSProperties => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  background: selected ? '#2563eb' : '#d0d4d8',
});

/**
 * Arrow-key navigation within the 3x3 grid, wrapping at edges (so e.g. ArrowRight from the last
 * column moves to the first column of the same row). This is the standard `role="radiogroup"`
 * keyboard contract (WAI-ARIA APG "Radio Group" pattern): arrow keys move focus *and* selection
 * together between radios in the group, rather than merely moving focus.
 */
function nextIndexForKey(key: string, row: number, col: number): { row: number; col: number } | undefined {
  switch (key) {
    case 'ArrowRight':
      return { row, col: (col + 1) % 3 };
    case 'ArrowLeft':
      return { row, col: (col + 2) % 3 };
    case 'ArrowDown':
      return { row: (row + 1) % 3, col };
    case 'ArrowUp':
      return { row: (row + 2) % 3, col };
    default:
      return undefined;
  }
}

/**
 * Puck custom field for an image's focal point: a 3x3 grid of the nine `object-position` presets
 * in {@link IMAGE_FOCAL_POINTS}. A grid of buttons a retail-staff author can point at is a much
 * friendlier control than a dropdown listing "Top left / Top center / ..." as text.
 *
 * Implemented as a WAI-ARIA `radiogroup` (nine mutually-exclusive `radio` cells) rather than nine
 * independent buttons: this is a single-choice control, so it should behave like one. That means:
 *   - one accessible group (`role="radiogroup"` + accessible name) containing nine
 *     `role="radio"` cells with `aria-checked` reflecting selection;
 *   - roving tabindex -- only the selected cell (or the first cell, if nothing is selected yet)
 *     is a tab stop, matching how native radio buttons behave;
 *   - arrow-key navigation between cells that moves both focus and selection, per the WAI-ARIA
 *     APG "Radio Group" keyboard pattern.
 */
export function FocalPointField({ field, name, value, onChange, readOnly, hint }: FocalPointFieldProps): JSX.Element {
  const selectedKey = isFocalPoint(value) ? value : undefined;
  const cellRefs = useRef(new Map<ImageFocalPoint, HTMLDivElement | null>());
  const helpId = useId();
  const hintId = hint ? helpId : undefined;

  const handleSelect = useCallback(
    (key: ImageFocalPoint) => {
      if (readOnly) return;
      onChange(key);
    },
    [onChange, readOnly]
  );

  const focusCell = useCallback((key: ImageFocalPoint) => {
    cellRefs.current.get(key)?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>, cell: typeof GRID_CELLS[number]) => {
      if (readOnly) return;

      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        handleSelect(cell.key);
        return;
      }

      const next = nextIndexForKey(e.key, cell.row, cell.col);
      if (!next) return;

      e.preventDefault();
      const nextCell = GRID_CELLS.find((c) => c.row === next.row && c.col === next.col);
      if (!nextCell) return;

      // Per the APG radiogroup pattern, arrow keys move focus AND selection together.
      handleSelect(nextCell.key);
      focusCell(nextCell.key);
    },
    [readOnly, handleSelect, focusCell]
  );

  // Roving tabindex: only the selected cell is a tab stop, falling back to the first cell when
  // nothing is selected yet (so the group is still reachable via a single Tab from outside it).
  const tabbableKey = selectedKey ?? GRID_CELLS[0].key;

  return (
    <FieldLabelWithHelp label={field.label ?? name} readOnly={readOnly} help={hint} helpId={hintId}>
      <div role='radiogroup' aria-label={field.label ?? name} aria-describedby={hintId} style={gridStyle}>
        {GRID_CELLS.map((cell) => {
          const selected = selectedKey === cell.key;
          return (
            <div
              key={cell.key}
              ref={(el) => {
                cellRefs.current.set(cell.key, el);
              }}
              role='radio'
              aria-checked={selected}
              aria-label={cell.label}
              aria-disabled={readOnly ? true : undefined}
              tabIndex={cell.key === tabbableKey ? 0 : -1}
              onClick={() => handleSelect(cell.key)}
              onKeyDown={(e) => handleKeyDown(e, cell)}
              data-testid={`focal-point-${name}-${cell.key}`}
              style={cellStyle(selected, readOnly)}
            >
              <span style={dotStyle(selected)} />
            </div>
          );
        })}
      </div>
    </FieldLabelWithHelp>
  );
}

/**
 * Binds a `hint` into a `FocalPointField` render function, mirroring `SelectField()`'s factory
 * shape in select-field.tsx -- Puck only supplies `{ field, name, value, onChange, readOnly }` to
 * a custom field's `render`, so a per-field `hint` has to be closed over here rather than passed
 * through Puck.
 */
function createFocalPointFieldWidget(hint?: string) {
  return function FocalPointFieldWidget(props: Omit<FocalPointFieldProps, 'hint'>): JSX.Element {
    return <FocalPointField {...props} hint={hint} />;
  };
}

/**
 * Builds a complete Puck `custom` focal-point field, including the `ai.schema` the AI plugin
 * requires for every `custom` field. Mirrors `selectField()`'s guard: a bare
 * `{ type: 'custom', render: FocalPointField }` works in the editor UI but 400s the entire AI chat
 * request the moment the AI plugin reaches it, so the enum is baked in here rather than left to
 * each call site to remember.
 *
 * `hint`, when provided, is available from the label help button and wired up via
 * `aria-describedby` on the `radiogroup` container -- mirroring `selectField()`'s `hint` param.
 * Deliberately kept separate from `aiInstructions`: the LLM copy explains how to *set* the field
 * from a prompt, the human copy explains what the control *does* to a retail-staff author looking
 * at the grid (e.g. that it only has an effect for a particular Crop setting).
 */
export function focalPointField(label: string, aiInstructions?: string, hint?: string): CustomField<string> {
  return {
    type: 'custom',
    label,
    render: createFocalPointFieldWidget(hint),
    ai: {
      schema: { type: 'string', enum: FOCAL_POINT_KEYS },
      ...(aiInstructions ? { instructions: aiInstructions } : {}),
    },
  };
}
